import { create } from 'zustand'
import { inNativeApp, nativeCall } from '../lib/nativeApp'
import { readViewPref, writeViewPref } from '../lib/viewPrefs'
import type { PhoneNodeStatus } from '../utilities/phoneNode'
import useNode from './useNode'

/**
 * This phone as a node, inside the noBGP app (mobile/): one status for the Nodes list, the Add
 * card and the node page. NobgpShell.nodeStatus answers it, with the node's id once registered.
 */
interface PhoneNodeState {
  status: PhoneNodeStatus | null
  removing: boolean
  /** The person chose not to see the Add card on the Nodes list. Add Node still offers it. */
  cardHidden: boolean
  refresh: () => Promise<void>
  start: () => Promise<void>
  /** Turns the node off, forgets its identity (the next Add makes a new node), deletes the record. */
  remove: () => Promise<void>
  hideCard: () => void
}

// A start makes every status read that began before it stale: a read answered after the start
// would put back the old "off" (review of app#501). One poll after a tap, never two.
let starts = 0
let poll: number | undefined

/** Thrown when this app build cannot remove the phone: its record is then kept. */
export const PHONE_REMOVE_UNAVAILABLE = 'This app cannot remove the phone. Update the noBGP app, then try again.'

const usePhoneNode = create<PhoneNodeState>()((set, get) => ({
  status: null,
  removing: false,
  cardHidden: readViewPref('hidePhoneCard'),
  refresh: async () => {
    if (!inNativeApp) return
    const seen = starts
    // A rejection is an app build with no node: nothing shows for it.
    const status = await nativeCall<PhoneNodeStatus>('NobgpShell', 'nodeStatus').catch(() => ({ state: 'unavailable' }))
    if (!get().removing && seen === starts) set({ status })
  },
  // ⚠ nodeStart answers before the VPN has done anything, so its answer can be a stale "stopped".
  // After a tap the status is read for a minute whatever it says (review of app#501, 7), and on
  // after that while it reads "stopped": Android reads "stopped" while its VPN prompt is open, and
  // a person can answer the prompt after the minute. Ten minutes at most.
  start: async () => {
    const mine = ++starts
    set(s => ({ status: { ...s.status, state: 'starting', approve_url: undefined, error: undefined } }))
    const status = await nativeCall<PhoneNodeStatus>('NobgpShell', 'nodeStart').catch(() => ({ state: 'failed' }))
    if (mine === starts) set({ status })
    window.clearInterval(poll)
    const t0 = Date.now()
    poll = window.setInterval(() => {
      const age = Date.now() - t0
      if (get().removing || age > 600_000 || (age > 60_000 && get().status?.state !== 'stopped')) {
        window.clearInterval(poll)
        poll = undefined
      } else void get().refresh()
    }, 1500)
  },
  // ⚠ The native half first. If the app cannot remove the phone (a build with no nodeRemove), the
  // record stays: deleting it would leave an agent that runs with a deleted identity. A refused
  // delete is thrown for the caller to show; the phone's own node is gone either way.
  remove: async () => {
    const id = get().status?.node_id
    window.clearInterval(poll)
    poll = undefined
    set({ removing: true })
    try {
      await nativeCall('NobgpShell', 'nodeRemove')
    } catch {
      set({ removing: false })
      throw new Error(PHONE_REMOVE_UNAVAILABLE)
    }
    try {
      if (id) await useNode.getState().delete(id, true)
    } finally {
      set({ status: { state: 'off' }, removing: false })
    }
  },
  hideCard: () => {
    writeViewPref('hidePhoneCard', true)
    set({ cardHidden: true })
  },
}))

/**
 * This phone's node id once it registered, else undefined: also while its VPN is off, so the list
 * keeps it on top and offers to turn it on. A primitive, for a selector.
 */
export const usePhoneNodeId = () => usePhoneNode(s => (s.removing ? undefined : s.status?.node_id || undefined))

export default usePhoneNode
